import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, X } from 'lucide-react';
import Fuse from 'fuse.js';
import { api } from '../services/api';
import { getProductUrl } from '../utils/urlHelpers';

const SearchBar = ({ onSearchSubmit, isMobile = false, onActiveChange }) => {
    const [isFocused, setIsFocused] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [products, setProducts] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const searchContainerRef = useRef(null);
    const inputRef = useRef(null);
    const navigate = useNavigate();

    const isActive = isFocused || !!searchValue;

    useEffect(() => {
        if (onActiveChange) {
            onActiveChange(isActive);
        }
    }, [isActive, onActiveChange]);

    // Load products on demand to prevent 429 Too Many Requests
    useEffect(() => {
        if (!isFocused || products.length > 0) return;

        const fetchProducts = async () => {
            try {
                const data = await api.getProducts();
                const allProducts = Array.isArray(data) ? data : (data?.data || []);
                setProducts(allProducts);
            } catch (error) {
                console.error('Error fetching products for search:', error);
            }
        };
        fetchProducts();
    }, [isFocused]);

    // Configure Fuse.js
    const fuse = new Fuse(products, {
        keys: ['name', 'category', 'description'],
        threshold: 0.3, // Fuzzy matching threshold
        distance: 100,
    });

    // Handle search input
    useEffect(() => {
        if (!searchValue.trim()) {
            setSearchResults([]);
            return;
        }

        setIsLoading(true);
        // Add small delay to avoid lagging while typing
        const timer = setTimeout(() => {
            const results = fuse.search(searchValue);
            // Get top 5 results
            setSearchResults(results.slice(0, 5).map(result => result.item));
            setIsLoading(false);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchValue, products]);

    // Click outside listener to close dropdown and blur
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                setIsFocused(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (searchValue.trim()) {
            setIsFocused(false);
            if (onSearchSubmit) {
                onSearchSubmit(searchValue);
            } else {
                navigate(`/arama?q=${encodeURIComponent(searchValue.trim())}`);
            }
            setSearchValue('');
            setSearchResults([]);
        }
    };

    const handleResultClick = (product) => {
        setIsFocused(false);
        setSearchValue('');
        setSearchResults([]);
        navigate(getProductUrl(product));
    };

    const formatPrice = (priceVal) => {
        return typeof priceVal === 'number'
            ? priceVal.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' ₺'
            : priceVal || 'Fiyat Sorunuz';
    };

    return (
        <div
            ref={searchContainerRef}
            className={`relative flex items-center transition-all duration-700 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] z-[100] group
        ${isActive ? 'w-full lg:w-80' : 'w-10 lg:w-12 lg:hover:w-80'}
      `}
            style={{ height: '44px' }}
        >
            {/* Background & Animations (Visible when active) */}
            <div
                className={`absolute inset-0 pointer-events-none transition-opacity duration-500 z-0 rounded-full
          ${isActive || isMobile ? 'opacity-100' : 'opacity-0 lg:group-hover:opacity-100'}`}
            >
                {/* Glow (Outer glow) using Carmen Gold colors */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] blur-[20px] overflow-hidden rounded-full">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] animate-spin-slow
            bg-[conic-gradient(from_0deg,rgba(212,175,55,0.05),rgba(212,175,55,0.4),rgba(212,175,55,0.05))]" />
                </div>

                {/* Border spinning animation */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%+2px)] h-[calc(100%+2px)] overflow-hidden rounded-full">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] animate-spin-slow
            bg-[conic-gradient(transparent,rgba(212,175,55,0.5),transparent_40%,rgba(212,175,55,0.5),transparent_90%)]" />
                </div>

                {/* Dark BG Overlay */}
                <div className="absolute inset-[1px] bg-white/95 lg:bg-white rounded-full z-[1]"
                    style={{
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)'
                    }}
                />
            </div>

            {/* Input Container */}
            <form
                onSubmit={handleSubmit}
                className="relative w-full h-full flex items-center z-10"
            >
                {/* Search Icon (Always visible) */}
                <div
                    className={`absolute left-0 w-10 lg:w-12 h-full flex items-center justify-center transition-colors duration-300 pointer-events-none
            ${isActive ? 'text-[#D4AF37]' : isMobile ? 'text-[#D4AF37]' : 'text-gray-500 lg:group-hover:text-[#D4AF37]'}`}
                >
                    <Search size={isMobile ? 20 : 18} strokeWidth={2} />
                </div>

                <input
                    ref={inputRef}
                    type="text"
                    className={`w-full h-full bg-transparent border-none outline-none text-[#1a1a1a] text-sm lg:text-sm pl-10 pr-10 transition-opacity duration-300
            ${isActive || isMobile ? 'opacity-100' : 'opacity-0 lg:group-hover:opacity-100 placeholder:text-transparent'}
            placeholder:text-[#8A8A8A] placeholder:font-light`}
                    placeholder="Ürün ara..."
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                />

                {/* Clear Button / Loader */}
                <div className={`absolute right-3 transition-opacity duration-300 ${isActive && searchValue ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                    {isLoading ? (
                        <Loader2 size={16} className="animate-spin text-[#D4AF37]" />
                    ) : (
                        <button
                            type="button"
                            onClick={() => {
                                setSearchValue('');
                                inputRef.current?.focus();
                            }}
                            aria-label="Aramayı temizle"
                            className="text-gray-400 hover:text-[#D4AF37] transition-colors p-1"
                        >
                            <X size={16} />
                        </button>
                    )}
                </div>
            </form>

            {/* Dropdown Menu */}
            {isActive && searchValue && searchResults.length > 0 && (
                <div className="absolute top-full -left-[48px] w-[calc(100vw-32px)] sm:-left-[48px] sm:w-[500px] lg:left-0 lg:w-auto lg:right-0 mt-3 bg-white rounded-2xl shadow-xl border border-[#D4AF37]/20 py-2 overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200">
                    <div className="max-h-[60vh] overflow-y-auto custom-scrollbar">
                        {searchResults.map((product) => (
                            <button
                                key={product.id}
                                onClick={() => handleResultClick(product)}
                                className="w-full flex items-center gap-4 px-4 py-3 hover:bg-[#FAF7F0] transition-colors border-b border-gray-50 last:border-0 text-left group/item"
                            >
                                {/* Product Image */}
                                <div className="w-12 h-12 flex-shrink-0 bg-gray-50 rounded-lg overflow-hidden border border-gray-100 flex items-center justify-center p-1">
                                    <img
                                        src={product.images?.[0] || product.image || '/placeholder-product.jpg'}
                                        alt={product.name}
                                        className="w-full h-full object-contain mix-blend-multiply group-hover/item:scale-110 transition-transform duration-300"
                                    />
                                </div>

                                {/* Product Info */}
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-semibold text-gray-900 truncate group-hover/item:text-[#D4AF37] transition-colors" style={{ fontFamily: "'Jost', sans-serif" }}>
                                        {product.name}
                                    </h4>
                                    <p className="text-[0.65rem] text-gray-500 truncate uppercase tracking-wider font-medium" style={{ fontFamily: "'Jost', sans-serif" }}>
                                        {product.category?.replace(/-/g, ' ')}
                                    </p>
                                </div>

                                {/* Price & Badge */}
                                <div className="text-right flex-shrink-0 flex flex-col items-end gap-0.5">
                                    <div className="flex items-center gap-1.5">
                                        {product.discountActive && product.originalPrice && (
                                            <div className="text-[0.7rem] text-gray-400 line-through font-medium" style={{ fontFamily: "'Jost', sans-serif" }}>
                                                {Number(product.originalPrice).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                                            </div>
                                        )}
                                        {product.discountActive && product.discountPercentage && (
                                            <div
                                                className="inline-flex items-center px-1.5 py-0.5 rounded-[4px] text-[0.6rem] font-bold tracking-wider"
                                                style={{ background: '#1a1a1a', color: '#D4AF37', border: '1px solid rgba(212,175,55,0.3)', fontFamily: "'Jost',sans-serif" }}
                                            >
                                                %{Math.round(parseFloat(product.discountPercentage))}
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="flex items-center gap-2 mt-0.5">
                                        <div className="flex items-baseline gap-0.5 whitespace-nowrap" style={{ fontFamily: "'Jost', sans-serif" }}>
                                            {(() => {
                                                const price = Number(product.basePrice || product.price || 0);
                                                const str = price.toLocaleString('tr-TR', { minimumFractionDigits: 2 });
                                                const idx = str.lastIndexOf(',');
                                                const int = str.slice(0, idx);
                                                const dec = str.slice(idx);
                                                return (
                                                  <>
                                                    <span className="text-[1rem] font-bold text-[#D4AF37] tracking-tight leading-none">
                                                      {int}<span className="text-[0.7rem] opacity-80">{dec}</span>
                                                    </span>
                                                    <span className="text-[0.9rem] text-[#b8941f] font-bold ml-0.5">₺</span>
                                                  </>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* View All Button */}
                    <div className="px-4 py-2 border-t border-gray-100 bg-gray-50/50">
                        <button
                            onClick={handleSubmit}
                            className="w-full text-center text-[0.7rem] font-bold text-[#D4AF37] hover:text-[#b3922d] transition-colors py-1 uppercase tracking-widest"
                            style={{ fontFamily: "'Jost', sans-serif" }}
                        >
                            Tüm "{searchValue}" sonuçlarını gör →
                        </button>
                    </div>
                </div>
            )}

            {/* No Results state */}
            {isActive && searchValue && searchResults.length === 0 && !isLoading && (
                <div className="absolute top-full left-0 right-0 mt-3 bg-white rounded-2xl shadow-xl border border-gray-100 p-6 text-center animate-in fade-in duration-200">
                    <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-3">
                        <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-900 mb-1">Sonuç bulamadık</p>
                    <p className="text-xs text-gray-500">Farklı kelimelerle arama yapmayı deneyin.</p>
                </div>
            )}

            {/* Global CSS for spinning animation used in border / glow */}
            <style jsx="true" global="true">{`
        @keyframes spin-slow {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 4s linear infinite;
        }
        
        /* Custom scrollbar for dropdown */
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #e5e7eb;
          border-radius: 10px;
        }
      `}</style>
        </div>
    );
};

export default SearchBar;
